/** The ground every generated cave starts from: a zoned random fill, then the inbox and the exit. */

import type { Rng } from '../../sim/rng';
import { between, progress } from './plan';
import type { Cell, Rect, Sketch } from './sketch';

/** Three rows above the inbox to one below, two cells either side. */
const POCKET = (c: Cell): Rect => ({ x: c.x - 2, y: c.y - 3, w: 5, h: 5 });
/** Two rows above the exit to one below, one cell either side. */
const DOORSTEP = (c: Cell): Rect => ({ x: c.x - 1, y: c.y - 2, w: 3, h: 4 });

/** Zones of this many cells get their own densities. */
const ZONE_W = 7;
const ZONE_H = 5;
/** How much a zone scales the odds: a few bare patches, a few heavy ones, averaging about 1. */
const ZONE_SCALE = [0.2, 0.6, 0.9, 1, 1.1, 1.4, 1.8];
/** A one-screen cave has too few zones for bare patches to even out: milder there. */
const SMALL_ZONE_SCALE = [0.7, 0.9, 1, 1.1, 1.3];

/**
 * Dirt with boulders (8-20 %) and diamonds (3-6 %, plus `extraGems` in the richer bonus caves),
 * both denser with depth, and a sprinkle of space. Each zone scales the odds up or down, so
 * boulders bunch into slopes and diamonds into seams instead of an even salt-and-pepper noise.
 */
export function baseFill(sk: Sketch, rng: Rng, depth: number, extraGems: number): void {
  const t = progress(depth);
  const boulders = 8 + 12 * t + between(rng, -1, 2);
  const diamonds = 3 + 3 * t + rng.int(2) + extraGems;
  const spaces = between(rng, 1, 3);
  const cols = Math.ceil(sk.w / ZONE_W);
  const rows = Math.ceil(sk.h / ZONE_H);
  const scale = sk.w < 40 ? SMALL_ZONE_SCALE : ZONE_SCALE;
  const rock: number[] = [];
  const gem: number[] = [];
  for (let i = 0; i < cols * rows; i++) {
    rock.push(scale[rng.int(scale.length)] ?? 1);
    gem.push(scale[rng.int(scale.length)] ?? 1);
  }
  for (let y = 1; y < sk.h - 1; y++) {
    for (let x = 1; x < sk.w - 1; x++) {
      const z = Math.floor(y / ZONE_H) * cols + Math.floor(x / ZONE_W);
      const rb = boulders * (rock[z] ?? 1) * 10;
      const rd = rb + diamonds * (gem[z] ?? 1) * 10;
      const r = rng.int(1000);
      sk.set(x, y, r < rb ? 'r' : r < rd ? 'd' : r < rd + spaces * 10 ? ' ' : '.');
    }
  }
}

/**
 * The inbox near one side wall, its safe pocket claimed so no feature lands on it. Creatures keep
 * further off still (`awayFromInbox`). In a bonus cave it hugs the wall, leaving the screen's
 * width for the puzzle.
 */
export function placeInbox(sk: Sketch, rng: Rng, bonus: boolean): Cell {
  const band = bonus ? 2 : 7;
  const west = rng.oneIn(2);
  const inbox = { x: west ? 2 + rng.int(band) : sk.w - 3 - rng.int(band), y: 1 + rng.int(sk.h - 2) };
  sk.set(inbox.x, inbox.y, 'P');
  sk.claim(POCKET(inbox));
  return inbox;
}

/** The exit towards the far side wall, far from the inbox, where its doorstep is still free. */
export function placeExit(sk: Sketch, rng: Rng, inbox: Cell, bonus: boolean): Cell | undefined {
  const band = bonus ? 6 : 7;
  const minApart = bonus ? 13 : 28;
  const west = inbox.x >= sk.w / 2;
  for (let i = 0; i < 60; i++) {
    const exit = { x: west ? 2 + rng.int(band) : sk.w - 3 - rng.int(band), y: 1 + rng.int(sk.h - 2) };
    if (Math.abs(inbox.x - exit.x) + Math.abs(inbox.y - exit.y) < minApart) continue;
    if (!sk.free(sk.clip(DOORSTEP(exit)))) continue;
    sk.set(exit.x, exit.y, 'X');
    sk.claim(DOORSTEP(exit));
    return exit;
  }
  return undefined;
}

/**
 * The safe pocket: nothing loose or hollow from three rows above the inbox to one below, two
 * cells either side, so nothing can fall on Digby as he hatches or on his first steps.
 */
export function clearPocket(sk: Sketch, inbox: Cell): void {
  settle(sk, POCKET(inbox));
}

/** The exit's doorstep: nothing loose round it, so the first landslide does not bury it. */
export function clearDoorstep(sk: Sketch, exit: Cell): void {
  settle(sk, DOORSTEP(exit));
}

/** Boulders, diamonds and space in the rect become dirt. */
function settle(sk: Sketch, r: Rect): void {
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const ch = sk.get(x, y);
      if (ch === 'r' || ch === 'd' || ch === ' ') sk.set(x, y, '.');
    }
  }
}
